import { useEffect, useState } from 'react';
import type { ProjectGraphResponse } from '@avisoma/shared';
import { indexOf } from './graphIndex';
import { graphDiff, type GraphChange } from './graphRefresh';

/** How long the "Graph updated" note stays. */
const NOTE_MS = 6000;

const nodeIdsOf = (graph: ProjectGraphResponse): ReadonlySet<string> => new Set(indexOf(graph).nodesById.keys());

/**
 * Watches the graph query's data for a newer snapshot (a Refresh, or a refetch once the data is stale). On one it
 * calls `onUpdate` with the new node ids, while rendering, so the page drops the ids that vanished before the view
 * is computed, and returns the change in nodes for NOTE_MS; `null` otherwise. The first load is no update.
 */
export function useGraphRefresh(
  graph: ProjectGraphResponse | undefined,
  onUpdate: (nodeIds: ReadonlySet<string>) => void,
): GraphChange | null {
  // The snapshot last seen; state rather than a ref, so it can be read while rendering.
  const [seen, setSeen] = useState(graph);
  const [change, setChange] = useState<GraphChange | null>(null);
  if (graph !== seen) {
    setSeen(graph);
    if (graph && seen && Date.parse(graph.generatedAt) > Date.parse(seen.generatedAt)) {
      const nextIds = nodeIdsOf(graph);
      setChange(graphDiff(nodeIdsOf(seen), nextIds));
      onUpdate(nextIds);
    }
  }
  useEffect(() => {
    if (change === null) return;
    const timer = setTimeout(() => setChange(null), NOTE_MS);
    return () => clearTimeout(timer);
  }, [change]);
  return change;
}
